import axios from "./HttpClient";

import * as React from "react";
import { GetUser, GetUserInfo } from "./IdentityAPI";
import { singletonHook } from "react-singleton-hook";
import { isArray, toPairs, some } from "lodash";
import { useMsal } from "@azure/msal-react";
import { GetServiceManagerUser } from "./ServiceManagerAPI";

const ROLE_CLAIM = "http://schemas.microsoft.com/ws/2008/06/identity/claims/role";
const NAME_CLAIM = "name";
const QUESTIONNAIRE_CLAIM = "sca-ser-questionnaire";
const QUESTIONNAIRE_TYPE_CLAIM = "sca-ser-questionnaire-type";
const PERMISSION_CLAIM = "sca-ide-permission";
const CAMPAIGN_CLAIM = "sca-ser-campaign";
const ORGANIZATION_CLAIM = "sca-organization";
export const MEMBERSHIPCLAIMTYPE = "sca-organization-membership";
export const CONTEXTCLAIMTYPE = "sca-context-identifier";

export interface Claim {
    type: string;
    value: string;
}

interface Permission {
    permission: string;
    organizationID: string;
}

export class Account {
    isAuthenticated: boolean;
    name: string;
    claims: Array<Claim>;

    fullName: string;
    firstName: string;
    lastName: string;
    displayName: string;
    isDisabled: boolean;

    id: string;
    email: string;
    emailConfirmed: boolean;
    permissions: Array<Permission>;

    data: any;

    constructor(account) {
        this.isAuthenticated = !!account.id;

        this.claims = account.claims || [];
        this.permissions = account.permissions || [];

        this.fullName = account.fullName;
        this.firstName = account.firstName;
        this.lastName = account.lastName;
        this.displayName = account.displayName;
        this.isDisabled = account.isDisabled;
        this.id = account.id;
        this.email = account.email;
        this.emailConfirmed = account.emailConfirmed;
        this.data = account.data;
    }

    isSingleOrganizationUser() {
        return this.claims.filter((c) => c.type == ORGANIZATION_CLAIM).length == 1 && !this.hasRole("Admin");
    }

    hasRole(role) {
        return this.claims.filter((c) => c.type == ROLE_CLAIM && this.claimValueEquals(c.value, role)).length > 0;
    }

    getClaim(claim) {
        let matched = this.claims.filter((c) => c.type == claim);
        if (matched.length > 0) {
            return matched[0].value;
        }
        return null;
    }

    getOrganization() {
        return this.getClaim(ORGANIZATION_CLAIM);
    }

    getContextID() {
        return this.getClaim(CONTEXTCLAIMTYPE);
    }

    claimValueEquals(claimValue, comparison) {
        if (isArray(claimValue)) {
            return some(claimValue, (value) => value == comparison);
        }
        return claimValue == comparison;
    }

    hasQuestionnaireAccess(questionnaire, ...accessType) {
        return (
            questionnaire &&
            (this.hasRole("Admin") ||
                this.claims
                    .filter((c) => c.type == QUESTIONNAIRE_CLAIM && c.value.indexOf(questionnaire) == 0)
                    .map((c) => +c.value.substring(c.value.indexOf(":") + 1))
                    .filter((c) => accessType.some((a) => (c | a) == c)).length > 0)
        );
    }

    hasAnyQuestionnaireAccess(...accessType) {
        return (
            this.hasRole("Admin") ||
            this.claims
                .filter((c) => c.type == QUESTIONNAIRE_CLAIM)
                .map((c) => +c.value.substring(c.value.indexOf(":") + 1))
                .filter((c) => accessType.some((a) => (c | a) == c)).length > 0
        );
    }

    hasQuestionnaireTypeAccess(questionnaireType, ...accessType) {
        return (
            questionnaireType &&
            (this.hasRole("Admin") ||
                this.claims
                    .filter((c) => c.type == QUESTIONNAIRE_TYPE_CLAIM && c.value.indexOf(questionnaireType) == 0)
                    .map((c) => +c.value.substring(c.value.indexOf(":") + 1))
                    .filter((c) => accessType.some((a) => (c | a) == c)).length > 0)
        );
    }

    hasAnyQuestionnaireTypeAccess(...accessType) {
        return (
            this.hasRole("Admin") ||
            this.claims
                .filter((c) => c.type == QUESTIONNAIRE_TYPE_CLAIM)
                .map((c) => +c.value.substring(c.value.indexOf(":") + 1))
                .filter((c) => accessType.some((a) => (c | a) == c)).length > 0
        );
    }

    hasCampaignAccess(campaign, ...accessType) {
        return (
            campaign &&
            (this.hasRole("Admin") ||
                this.claims
                    .filter((c) => c.type == CAMPAIGN_CLAIM && c.value.indexOf(campaign) == 0)
                    .map((c) => +c.value.substring(c.value.indexOf(":") + 1))
                    .filter((c) => accessType.some((a) => (c | a) == c)).length > 0)
        );
    }

    hasAnyCampaignAccess(...accessType) {
        return (
            this.hasRole("Admin") ||
            this.claims
                .filter((c) => c.type == CAMPAIGN_CLAIM)
                .map((c) => +c.value.substring(c.value.indexOf(":") + 1))
                .filter((c) => accessType.some((a) => (c | a) == c)).length > 0
        );
    }

    hasPermission(permission) {
        var organization = this.getClaim(ORGANIZATION_CLAIM);
        return this.hasRole("Admin") || this.permissions.map((c) => c.permission + ":" + c.organizationID).filter((c) => c == permission + ":" + organization).length > 0;
    }
}

const init = { user: new Account({}), setUser: () => {} };

const userFactory = () => {
    const [user, setUser] = React.useState(new Account({}));

    React.useEffect(() => {
        GetUser().then((res) => {
            GetServiceManagerUser().then((res2) => {
                let user = new Account(res.data);
                user.claims.push(...res2.data.identity.claims)
                setUser(user);
            });
        });
    }, []);

    return { user, setUser };
};

export const useUser = singletonHook(init, userFactory);

export const getUser = async () => {
    const res = await GetUser();
    return new Account(res.data);
};
